import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  Calendar,
  CalendarIcon,
  FileText,
  Download,
  Mail,
  Printer,
  Building2,
  Users,
  Loader2,
  Send
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StaffHoursPage() {
  const { user, isAdmin, isSuperUser } = useAuth();
  const isStaff = user?.user_type === 'staff';
  const [activeTab, setActiveTab] = useState('entry');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Hours Entry State
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [cashiers, setCashiers] = useState([]);
  const [hoursEntries, setHoursEntries] = useState([]);
  
  // Daily Summary State (NEW - to verify saved hours)
  const [dailySummaryStore, setDailySummaryStore] = useState('');
  const [dailySummaryStartDate, setDailySummaryStartDate] = useState(null);
  const [dailySummaryEndDate, setDailySummaryEndDate] = useState(null);
  const [dailySummaryData, setDailySummaryData] = useState([]);
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [dailySummaryCashiers, setDailySummaryCashiers] = useState([]);
  
  // Time Sheet Summary State
  const [summaryStartDate, setSummaryStartDate] = useState(null);
  const [summaryEndDate, setSummaryEndDate] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [editableSummary, setEditableSummary] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailComments, setEmailComments] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const reportRef = useRef(null);

  useEffect(() => {
    fetchStores();
    // Set default date range to LAST MONTH
    const now = new Date();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    setSummaryStartDate(lastMonthStart);
    setSummaryEndDate(lastMonthEnd);
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchCashiers(selectedStore);
      fetchHoursForDate();
    }
  }, [selectedStore, selectedDate]);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load stores');
      setLoading(false);
    }
  };

  const fetchCashiers = async (storeId) => {
    try {
      const response = await axios.get(`${API}/settings/cashiers`, {
        params: { store_id: storeId }
      });
      setCashiers(response.data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Failed to load staff');
    }
  };

  const fetchHoursForDate = async () => {
    try {
      const dateStr = selectedDate instanceof Date ? format(selectedDate, 'yyyy-MM-dd') : selectedDate;
      const response = await axios.get(`${API}/staff-hours`, {
        params: { store_id: selectedStore, date: dateStr }
      });
      
      // Convert to editable format
      const entries = response.data.map(h => ({
        id: h.id,
        staff_id: h.staff_id,
        hours: h.hours,
        hours_type: h.hours_type
      }));
      
      setHoursEntries(entries.length > 0 ? entries : [{ staff_id: '', hours: '', hours_type: 'working' }]);
    } catch (error) {
      console.error('Failed to load hours:', error);
      setHoursEntries([{ staff_id: '', hours: '', hours_type: 'working' }]);
    }
  };

  const addHoursEntry = () => {
    setHoursEntries([...hoursEntries, { staff_id: '', hours: '', hours_type: 'working' }]);
  };

  const removeHoursEntry = (index) => {
    const updated = hoursEntries.filter((_, i) => i !== index);
    setHoursEntries(updated.length > 0 ? updated : [{ staff_id: '', hours: '', hours_type: 'working' }]);
  };

  const updateHoursEntry = (index, field, value) => {
    const updated = [...hoursEntries];
    updated[index] = { ...updated[index], [field]: value };
    setHoursEntries(updated);
  };

  const saveHours = async () => {
    if (!selectedStore || !selectedDate) {
      toast.error('Please select store and date');
      return;
    }
    
    const validEntries = hoursEntries.filter(e => e.staff_id && parseFloat(e.hours) > 0);
    const dateStr = selectedDate instanceof Date ? format(selectedDate, 'yyyy-MM-dd') : selectedDate;
    
    setSaving(true);
    try {
      await axios.post(`${API}/staff-hours/batch`, {
        store_id: selectedStore,
        date: dateStr,
        entries: validEntries.map(e => ({
          staff_id: e.staff_id,
          hours: parseFloat(e.hours) || 0,
          hours_type: e.hours_type
        }))
      });
      toast.success('Hours saved successfully');
      fetchHoursForDate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save hours');
    } finally {
      setSaving(false);
    }
  };

  const loadSummary = async () => {
    if (selectedStores.length === 0) {
      toast.error('Please select at least one store');
      return;
    }
    if (!summaryStartDate || !summaryEndDate) {
      toast.error('Please select date range');
      return;
    }
    
    const startStr = summaryStartDate instanceof Date ? format(summaryStartDate, 'yyyy-MM-dd') : summaryStartDate;
    const endStr = summaryEndDate instanceof Date ? format(summaryEndDate, 'yyyy-MM-dd') : summaryEndDate;
    
    setSummaryLoading(true);
    try {
      const response = await axios.get(`${API}/staff-hours/summary`, {
        params: {
          store_ids: selectedStores.join(','),
          start_date: startStr,
          end_date: endStr
        }
      });
      setSummaryData(response.data);
      // Create editable copy
      setEditableSummary(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      toast.error('Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Load Daily Summary - shows raw hours entries from database
  const loadDailySummary = async () => {
    if (!dailySummaryStore) {
      toast.error('Please select a store');
      return;
    }
    if (!dailySummaryStartDate || !dailySummaryEndDate) {
      toast.error('Please select date range');
      return;
    }
    
    const startStr = dailySummaryStartDate instanceof Date ? format(dailySummaryStartDate, 'yyyy-MM-dd') : dailySummaryStartDate;
    const endStr = dailySummaryEndDate instanceof Date ? format(dailySummaryEndDate, 'yyyy-MM-dd') : dailySummaryEndDate;
    
    setDailySummaryLoading(true);
    try {
      // Fetch hours entries
      const hoursResponse = await axios.get(`${API}/staff-hours`, {
        params: {
          store_id: dailySummaryStore,
          start_date: startStr,
          end_date: endStr
        }
      });
      
      // Fetch cashiers for this store to get names
      const cashiersResponse = await axios.get(`${API}/settings/cashiers`, {
        params: { store_id: dailySummaryStore }
      });
      
      setDailySummaryData(hoursResponse.data || []);
      setDailySummaryCashiers(cashiersResponse.data || []);
    } catch (error) {
      console.error('Failed to load daily summary:', error);
      toast.error('Failed to load daily summary');
    } finally {
      setDailySummaryLoading(false);
    }
  };

  // Helper to get cashier name from ID
  const getCashierName = (staffId) => {
    const cashier = dailySummaryCashiers.find(c => c.id === staffId || c.cashier_id === staffId);
    if (cashier) {
      return `${cashier.first_name || cashier.name || ''} ${cashier.last_name || ''}`.trim() || staffId;
    }
    return staffId; // Return raw ID if not found
  };

  const updateEditableSummary = (storeIndex, staffIndex, field, value) => {
    const updated = { ...editableSummary };
    updated.stores[storeIndex].staff[staffIndex][field] = parseFloat(value) || 0;
    // Recalculate total
    const staff = updated.stores[storeIndex].staff[staffIndex];
    staff.total_hours = (staff.working_hours || 0) + (staff.holiday_hours || 0) + (staff.sick_hours || 0);
    setEditableSummary(updated);
  };

  const toggleStoreSelection = (storeId) => {
    setSelectedStores(prev => 
      prev.includes(storeId) 
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const selectAllStores = () => {
    setSelectedStores(stores.map(s => s.id));
  };

  const printReport = () => {
    window.print();
  };

  const downloadPDF = () => {
    // For now, use browser print to PDF
    window.print();
  };

  const sendEmailReport = async () => {
    if (!emailAddress.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    if (!editableSummary || !editableSummary.stores?.length) {
      toast.error('No report data to send');
      return;
    }
    
    setSendingEmail(true);
    try {
      const startStr = summaryStartDate instanceof Date ? format(summaryStartDate, 'yyyy-MM-dd') : summaryStartDate;
      const endStr = summaryEndDate instanceof Date ? format(summaryEndDate, 'yyyy-MM-dd') : summaryEndDate;
      
      const response = await axios.post(`${API}/staff-hours/send-report`, {
        email: emailAddress.trim(),
        report_data: editableSummary,
        start_date: startStr,
        end_date: endStr,
        comments: emailComments.trim()
      });
      
      if (response.data.status === 'sent') {
        toast.success(response.data.message);
        setEmailDialogOpen(false);
        setEmailAddress('');
        setEmailComments('');
      } else if (response.data.status === 'not_configured') {
        toast.error('Email not configured. Please configure SMTP settings in System Settings.');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getStaffName = (staffId) => {
    const staff = cashiers.find(c => c.id === staffId);
    if (!staff) return 'Unknown';
    return `${staff.first_name || staff.name || ''} ${staff.last_name || ''}`.trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Staff Working Hours
          </h1>
          <p className="text-muted-foreground">
            Track and manage staff working hours for payroll
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full max-w-lg ${isStaff ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <TabsTrigger value="entry" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Hours Entry
          </TabsTrigger>
          <TabsTrigger value="daily-summary" className="flex items-center gap-2" data-testid="daily-summary-tab">
            <Users className="h-4 w-4" />
            Daily Summary
          </TabsTrigger>
          {!isStaff && (
            <TabsTrigger value="summary" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Timesheet Summary
            </TabsTrigger>
          )}
        </TabsList>

        {/* Hours Entry Tab */}
        <TabsContent value="entry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Calendar className="h-5 w-5 text-primary" />
                Daily Hours Entry
              </CardTitle>
              <CardDescription>
                Enter working hours for staff members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Store and Date Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <Label>Select Store</Label>
                  <Select value={selectedStore} onValueChange={setSelectedStore}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-start text-left font-normal bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="hours-entry-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {selectedStore && (
                <>
                  {/* Hours Entry Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 font-medium">Staff Name</th>
                          <th className="text-left p-3 font-medium w-32">Hours</th>
                          <th className="text-left p-3 font-medium w-40">Type</th>
                          <th className="text-center p-3 font-medium w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hoursEntries.map((entry, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-3">
                              <Select
                                value={entry.staff_id}
                                onValueChange={(value) => updateHoursEntry(index, 'staff_id', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select staff" />
                                </SelectTrigger>
                                <SelectContent>
                                  {cashiers.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.first_name || c.name} {c.last_name || ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3">
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="0"
                                value={entry.hours}
                                onChange={(e) => updateHoursEntry(index, 'hours', e.target.value)}
                                className="font-mono"
                              />
                            </td>
                            <td className="p-3">
                              <Select
                                value={entry.hours_type}
                                onValueChange={(value) => updateHoursEntry(index, 'hours_type', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="working">Working</SelectItem>
                                  <SelectItem value="holiday">Holiday</SelectItem>
                                  <SelectItem value="sick">Sick</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeHoursEntry(index)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <Button variant="outline" onClick={addHoursEntry}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Staff Entry
                    </Button>
                    <Button onClick={saveHours} disabled={saving}>
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Hours
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Today's Summary */}
                  {hoursEntries.some(e => e.staff_id && parseFloat(e.hours) > 0) && (
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-medium mb-3">Today's Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Working:</span>
                          <span className="ml-2 font-mono font-bold">
                            {hoursEntries.filter(e => e.hours_type === 'working').reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0).toFixed(1)} hrs
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Holiday:</span>
                          <span className="ml-2 font-mono font-bold text-blue-600">
                            {hoursEntries.filter(e => e.hours_type === 'holiday').reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0).toFixed(1)} hrs
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Sick:</span>
                          <span className="ml-2 font-mono font-bold text-amber-600">
                            {hoursEntries.filter(e => e.hours_type === 'sick').reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0).toFixed(1)} hrs
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Summary Tab - Shows raw hours data from database */}
        <TabsContent value="daily-summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Users className="h-5 w-5 text-primary" />
                Daily Hours Summary
              </CardTitle>
              <CardDescription>
                View raw hours data saved in the database for each day
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <Label>Select Store</Label>
                  <Select value={dailySummaryStore} onValueChange={setDailySummaryStore}>
                    <SelectTrigger data-testid="daily-summary-store-select">
                      <SelectValue placeholder="Choose a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-start text-left font-normal bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="daily-summary-start-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dailySummaryStartDate ? format(dailySummaryStartDate, 'PPP') : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dailySummaryStartDate}
                        onSelect={(date) => date && setDailySummaryStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-start text-left font-normal bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="daily-summary-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dailySummaryEndDate ? format(dailySummaryEndDate, 'PPP') : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dailySummaryEndDate}
                        onSelect={(date) => date && setDailySummaryEndDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-end">
                  <Button onClick={loadDailySummary} disabled={dailySummaryLoading} className="w-full" data-testid="load-daily-summary-btn">
                    {dailySummaryLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Load Data
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Results */}
              {dailySummaryData.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">
                      Found {dailySummaryData.length} hour entries
                    </h3>
                    <span className="text-sm text-muted-foreground">
                      {dailySummaryStartDate && dailySummaryEndDate && 
                        `${format(dailySummaryStartDate, 'dd MMM yyyy')} - ${format(dailySummaryEndDate, 'dd MMM yyyy')}`
                      }
                    </span>
                  </div>
                  
                  {/* Group by date */}
                  {(() => {
                    // Group entries by date
                    const groupedByDate = dailySummaryData.reduce((acc, entry) => {
                      const dateKey = entry.date?.substring(0, 10) || entry.date;
                      if (!acc[dateKey]) {
                        acc[dateKey] = [];
                      }
                      acc[dateKey].push(entry);
                      return acc;
                    }, {});
                    
                    const sortedDates = Object.keys(groupedByDate).sort().reverse();
                    
                    return (
                      <div className="space-y-4">
                        {sortedDates.map(date => (
                          <div key={date} className="border rounded-lg overflow-hidden">
                            <div className="bg-muted/50 px-4 py-2 font-semibold flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {date}
                              <span className="text-sm font-normal text-muted-foreground ml-2">
                                ({groupedByDate[date].length} entries)
                              </span>
                            </div>
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-muted/20">
                                  <th className="text-left p-3 font-medium">Staff Name</th>
                                  <th className="text-center p-3 font-medium">Hours</th>
                                  <th className="text-center p-3 font-medium">Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupedByDate[date].map((entry, idx) => (
                                  <tr key={entry.id || idx} className="border-b hover:bg-muted/10">
                                    <td className="p-3">{getCashierName(entry.staff_id)}</td>
                                    <td className="p-3 text-center font-semibold">{entry.hours}</td>
                                    <td className="p-3 text-center">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        entry.hours_type === 'working' ? 'bg-green-100 text-green-700' :
                                        entry.hours_type === 'holiday' ? 'bg-blue-100 text-blue-700' :
                                        'bg-amber-100 text-amber-700'
                                      }`}>
                                        {entry.hours_type}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                dailySummaryStore && dailySummaryStartDate && dailySummaryEndDate && !dailySummaryLoading && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No hours data found for this store and date range.</p>
                    <p className="text-sm mt-2">Try selecting a different date range or add hours via the "Hours Entry" tab.</p>
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Sheet Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <FileText className="h-5 w-5 text-primary" />
                Time Sheet Summary Report
              </CardTitle>
              <CardDescription>
                Generate payroll reports for selected stores and date range
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-start text-left font-normal bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="summary-start-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {summaryStartDate ? format(summaryStartDate, 'PPP') : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={summaryStartDate}
                        onSelect={(date) => date && setSummaryStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full justify-start text-left font-normal bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="summary-end-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {summaryEndDate ? format(summaryEndDate, 'PPP') : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={summaryEndDate}
                        onSelect={(date) => date && setSummaryEndDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-end">
                  <Button onClick={loadSummary} disabled={summaryLoading} className="w-full">
                    {summaryLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load Data'
                    )}
                  </Button>
                </div>
              </div>

              {/* Store Selection */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-medium">Select Stores</Label>
                  <Button variant="link" size="sm" onClick={selectAllStores}>
                    Select All
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {stores.map(store => (
                    <div key={store.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`store-${store.id}`}
                        checked={selectedStores.includes(store.id)}
                        onCheckedChange={() => toggleStoreSelection(store.id)}
                      />
                      <label
                        htmlFor={`store-${store.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {store.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Report Preview */}
              {editableSummary && editableSummary.stores?.length > 0 && (
                <>
                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-2 print:hidden">
                    <Button variant="outline" onClick={printReport}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                    <Button variant="outline" onClick={downloadPDF}>
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button variant="outline" onClick={() => setEmailDialogOpen(true)}>
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                  </div>

                  {/* Report Content */}
                  <div ref={reportRef} className="print:p-4" id="timesheet-report">
                    {/* Report Heading */}
                    <div className="mb-6 text-center border-b pb-4">
                      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                        Time Sheet Summary
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Period: {format(summaryStartDate, 'dd MMM yyyy')} to {format(summaryEndDate, 'dd MMM yyyy')}
                      </p>
                    </div>

                    {editableSummary.stores.map((store, storeIndex) => {
                      // Split staff by pay type
                      const hourlyStaff = store.staff.filter(s => (s.pay_type || 'hourly') === 'hourly');
                      const monthlyStaff = store.staff.filter(s => s.pay_type === 'monthly');
                      
                      // Get original index mapping for updates
                      const getOriginalIndex = (staff) => store.staff.findIndex(s => s.staff_id === staff.staff_id);
                      
                      return (
                        <div key={store.store_id} className="mb-8 border rounded-lg p-4 print:border-black">
                          {/* Store Header */}
                          <div className="mb-4 pb-3 border-b">
                            {store.parent_company && (
                              <div className="text-sm text-muted-foreground mb-1">
                                <Building2 className="h-4 w-4 inline mr-1" />
                                Company: <span className="font-medium text-foreground">{store.parent_company}</span>
                              </div>
                            )}
                            <h3 className="text-lg font-bold">{store.store_name}</h3>
                          </div>

                          {/* HOURLY STAFF SECTION */}
                          {hourlyStaff.length > 0 && (
                            <div className="mb-6">
                              <h4 className="text-sm font-semibold text-green-600 mb-2 flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Hourly Staff
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead className="bg-green-500/10">
                                    <tr>
                                      <th className="text-left p-2 font-medium border-b w-[120px]">First Name</th>
                                      <th className="text-left p-2 font-medium border-b w-[120px]">Last Name</th>
                                      <th className="text-center p-2 font-medium border-b w-[90px]">Contracted</th>
                                      <th className="text-center p-2 font-medium border-b w-[90px]">Working</th>
                                      <th className="text-center p-2 font-medium border-b w-[90px]">Holiday</th>
                                      <th className="text-center p-2 font-medium border-b w-[90px]">Sick</th>
                                      <th className="text-center p-2 font-medium border-b w-[70px]">Total</th>
                                      <th className="text-center p-2 font-medium border-b w-[50px] print:hidden"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {hourlyStaff.map((staff) => {
                                      const staffIndex = getOriginalIndex(staff);
                                      return (
                                        <tr key={staff.staff_id} className="border-b hover:bg-muted/20">
                                          <td className="p-2">{staff.first_name}</td>
                                          <td className="p-2">{staff.last_name}</td>
                                          <td className="p-2 text-center font-mono">{staff.contracted_hours || 0}</td>
                                          <td className="p-2 text-center">
                                            <Input
                                              type="number"
                                              step="0.5"
                                              value={staff.working_hours || 0}
                                              onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'working_hours', e.target.value)}
                                              className="w-16 h-7 text-center font-mono text-sm mx-auto print:hidden"
                                            />
                                            <span className="font-mono hidden print:inline">{staff.working_hours || 0}</span>
                                          </td>
                                          <td className="p-2 text-center">
                                            <Input
                                              type="number"
                                              step="0.5"
                                              value={staff.holiday_hours || 0}
                                              onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'holiday_hours', e.target.value)}
                                              className="w-16 h-7 text-center font-mono text-sm mx-auto print:hidden"
                                            />
                                            <span className="font-mono hidden print:inline text-blue-600">{staff.holiday_hours || 0}</span>
                                          </td>
                                          <td className="p-2 text-center">
                                            <Input
                                              type="number"
                                              step="0.5"
                                              value={staff.sick_hours || 0}
                                              onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'sick_hours', e.target.value)}
                                              className="w-16 h-7 text-center font-mono text-sm mx-auto print:hidden"
                                            />
                                            <span className="font-mono hidden print:inline text-amber-600">{staff.sick_hours || 0}</span>
                                          </td>
                                          <td className="p-2 text-center font-mono font-bold">
                                            {staff.total_hours || 0}
                                          </td>
                                          <td className="p-2 text-center print:hidden">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                              onClick={() => {
                                                setEditableSummary(prev => ({
                                                  ...prev,
                                                  stores: prev.stores.map((s, sIdx) => 
                                                    sIdx === storeIndex 
                                                      ? { ...s, staff: s.staff.filter((_, sfIdx) => sfIdx !== staffIndex) }
                                                      : s
                                                  )
                                                }));
                                              }}
                                              title="Remove from report"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot className="bg-green-500/10">
                                    <tr className="border-t-2">
                                      <td colSpan={3} className="p-2 font-medium">Hourly Totals</td>
                                      <td className="p-2 text-center font-mono font-bold">
                                        {hourlyStaff.reduce((sum, s) => sum + (s.working_hours || 0), 0).toFixed(1)}
                                      </td>
                                      <td className="p-2 text-center font-mono font-bold text-blue-600">
                                        {hourlyStaff.reduce((sum, s) => sum + (s.holiday_hours || 0), 0).toFixed(1)}
                                      </td>
                                      <td className="p-2 text-center font-mono font-bold text-amber-600">
                                        {hourlyStaff.reduce((sum, s) => sum + (s.sick_hours || 0), 0).toFixed(1)}
                                      </td>
                                      <td className="p-2 text-center font-mono font-bold text-primary">
                                        {hourlyStaff.reduce((sum, s) => sum + (s.total_hours || 0), 0).toFixed(1)}
                                      </td>
                                      <td className="print:hidden"></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* MONTHLY STAFF SECTION */}
                          {monthlyStaff.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-blue-600 mb-2 flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Monthly Salaried Staff
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead className="bg-blue-500/10">
                                    <tr>
                                      <th className="text-left p-2 font-medium border-b w-[120px]">First Name</th>
                                      <th className="text-left p-2 font-medium border-b w-[120px]">Last Name</th>
                                      <th className="text-center p-2 font-medium border-b w-[110px]">Monthly Salary</th>
                                      <th className="text-center p-2 font-medium border-b w-[110px]">Holiday Amount</th>
                                      <th className="text-center p-2 font-medium border-b w-[90px]">Sick Days</th>
                                      <th className="text-center p-2 font-medium border-b w-[50px] print:hidden"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {monthlyStaff.map((staff) => {
                                      const staffIndex = getOriginalIndex(staff);
                                      return (
                                        <tr key={staff.staff_id} className="border-b hover:bg-muted/20">
                                          <td className="p-2">{staff.first_name}</td>
                                          <td className="p-2">{staff.last_name}</td>
                                          <td className="p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                              <span className="text-xs print:hidden">£</span>
                                              <Input
                                                type="number"
                                                step="0.01"
                                                value={staff.monthly_salary || 0}
                                                onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'monthly_salary', e.target.value)}
                                                className="w-24 h-7 text-center font-mono text-sm print:hidden"
                                              />
                                              <span className="font-mono hidden print:inline">£{(staff.monthly_salary || 0).toFixed(2)}</span>
                                            </div>
                                          </td>
                                          <td className="p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                              <span className="text-xs print:hidden">£</span>
                                              <Input
                                                type="number"
                                                step="0.01"
                                                value={staff.holiday_amount || 0}
                                                onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'holiday_amount', e.target.value)}
                                                className="w-20 h-7 text-center font-mono text-sm print:hidden"
                                              />
                                              <span className="font-mono hidden print:inline text-blue-600">£{(staff.holiday_amount || 0).toFixed(2)}</span>
                                            </div>
                                          </td>
                                          <td className="p-2 text-center">
                                            <Input
                                              type="number"
                                              step="1"
                                              value={staff.sick_days || 0}
                                              onChange={(e) => updateEditableSummary(storeIndex, staffIndex, 'sick_days', e.target.value)}
                                              className="w-16 h-7 text-center font-mono text-sm mx-auto print:hidden"
                                            />
                                            <span className="font-mono hidden print:inline text-amber-600">{staff.sick_days || 0}</span>
                                          </td>
                                          <td className="p-2 text-center print:hidden">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                              onClick={() => {
                                                setEditableSummary(prev => ({
                                                  ...prev,
                                                  stores: prev.stores.map((s, sIdx) => 
                                                    sIdx === storeIndex 
                                                      ? { ...s, staff: s.staff.filter((_, sfIdx) => sfIdx !== staffIndex) }
                                                      : s
                                                  )
                                                }));
                                              }}
                                              title="Remove from report"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot className="bg-blue-500/10">
                                    <tr className="border-t-2">
                                      <td colSpan={2} className="p-2 font-medium">Monthly Totals</td>
                                      <td className="p-2 text-center font-mono font-bold">
                                        £{monthlyStaff.reduce((sum, s) => sum + (s.monthly_salary || 0), 0).toFixed(2)}
                                      </td>
                                      <td className="p-2 text-center font-mono font-bold text-blue-600">
                                        £{monthlyStaff.reduce((sum, s) => sum + (s.holiday_amount || 0), 0).toFixed(2)}
                                      </td>
                                      <td className="p-2 text-center font-mono font-bold text-amber-600">
                                        {monthlyStaff.reduce((sum, s) => sum + (s.sick_days || 0), 0)}
                                      </td>
                                      <td className="print:hidden"></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Comments for Accountant */}
                  <div className="mt-6 p-4 border rounded-lg bg-muted/20 print:hidden">
                    <Label htmlFor="accountant-comments" className="text-sm font-medium mb-2 block">
                      Comments for Accountant
                    </Label>
                    <textarea
                      id="accountant-comments"
                      placeholder="Add any notes or comments for the accountant (will be included in PDF)..."
                      value={emailComments}
                      onChange={(e) => setEmailComments(e.target.value)}
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  </div>
                </>
              )}

              {editableSummary && editableSummary.stores?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No data found for the selected stores and date range.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #timesheet-report, #timesheet-report * {
            visibility: visible;
          }
          #timesheet-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:inline {
            display: inline !important;
          }
        }
      `}</style>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Send Time Sheet Report
            </DialogTitle>
            <DialogDescription>
              Enter the email address to send the report to. The report will include all selected stores and their staff hours for the period {format(summaryStartDate, 'dd MMM yyyy')} to {format(summaryEndDate, 'dd MMM yyyy')}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="recipient@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </div>
            {editableSummary && (
              <div className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                <p><strong>Report includes:</strong></p>
                <ul className="list-disc list-inside mt-1">
                  <li>{editableSummary.stores?.length || 0} store(s)</li>
                  <li>{editableSummary.stores?.reduce((sum, s) => sum + (s.staff?.length || 0), 0) || 0} staff member(s)</li>
                  {emailComments && <li>Comments included</li>}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={sendEmailReport} disabled={sendingEmail}>
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
